import { Colors } from 'styles/index';
import { ScreenName, type WorkspaceLayoutPreset } from 'generated/graphql';

const CHARACTER_PIXELS_WIDTH = 7;

const WINDOW_LABELS: Record<ScreenName, { label: string; shortLabel: string }> = {
  [ScreenName.Worklist]: { label: 'Worklist', shortLabel: 'WL' },
  [ScreenName.Viewer0]: { label: 'Viewer 1', shortLabel: 'V1' },
  [ScreenName.Viewer1]: { label: 'Viewer 2', shortLabel: 'V2' },
  [ScreenName.Reporter]: { label: 'Reporter', shortLabel: 'R' },
  [ScreenName.PatientJacket]: { label: 'Patient Jacket', shortLabel: 'PJ' },
  [ScreenName.Admin]: { label: 'Admin', shortLabel: 'A' },
};

const WINDOW_COLORS: Partial<Record<ScreenName, string>> = {
  [ScreenName.Viewer0]: Colors.green2,
  [ScreenName.Viewer1]: Colors.teal2,
  [ScreenName.Reporter]: Colors.blue7,
  [ScreenName.Worklist]: Colors.purple2,
  [ScreenName.PatientJacket]: Colors.pink2,
  [ScreenName.Admin]: Colors.orange2,
};

export type DisplayGeometry = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type ArrangementBounds = {
  minLeft: number;
  minTop: number;
  width: number;
  height: number;
};

export type CanvasDimensions = {
  width: number;
  height: number;
  contentWidth: number;
  contentHeight: number;
};

export const getWindowColor = (screen: ScreenName) => WINDOW_COLORS[screen] ?? Colors.gray7;

export const getWindowName = (screen: ScreenName) => WINDOW_LABELS[screen]?.label ?? String(screen);

export const getArrangementBounds = (
  displays: WorkspaceLayoutPreset['displaySlots']
): ArrangementBounds => {
  const minLeft = Math.min(...displays.map((display) => display.left));
  const minTop = Math.min(...displays.map((display) => display.top));
  const maxRight = Math.max(...displays.map((display) => display.left + display.width));
  const maxBottom = Math.max(...displays.map((display) => display.top + display.height));

  return {
    minLeft,
    minTop,
    width: Math.max(1, maxRight - minLeft),
    height: Math.max(1, maxBottom - minTop),
  };
};

export const getCanvasDimensions = ({
  arrangement,
  canvasMaxWidth,
  canvasVerticalPadding,
}: {
  arrangement: ArrangementBounds;
  canvasMaxWidth: number;
  canvasVerticalPadding: number;
}): CanvasDimensions => {
  const width = canvasMaxWidth;
  const height =
    (arrangement.height / arrangement.width) * canvasMaxWidth + canvasVerticalPadding * 2;

  return {
    width,
    height,
    contentWidth: width,
    contentHeight: height - canvasVerticalPadding * 2,
  };
};

export const getDisplayGeometry = ({
  display,
  arrangement,
  canvas,
  canvasVerticalPadding,
  displayGap,
}: {
  display: WorkspaceLayoutPreset['displaySlots'][number];
  arrangement: ArrangementBounds;
  canvas: CanvasDimensions;
  canvasVerticalPadding: number;
  displayGap: number;
}): DisplayGeometry => {
  return {
    left: ((display.left - arrangement.minLeft) / arrangement.width) * canvas.contentWidth,
    top:
      canvasVerticalPadding +
      ((display.top - arrangement.minTop) / arrangement.height) * canvas.contentHeight,
    width: (display.width / arrangement.width) * canvas.contentWidth - displayGap,
    height: (display.height / arrangement.height) * canvas.contentHeight - displayGap,
  };
};

export const getWindowLabel = ({
  screen,
  renderedWidth,
}: {
  screen: ScreenName;
  renderedWidth: number;
}) => {
  const { label, shortLabel } = WINDOW_LABELS[screen] ?? {
    label: String(screen),
    shortLabel: String(screen).slice(0, 2),
  };

  const canFitFullLabel = renderedWidth > label.length * CHARACTER_PIXELS_WIDTH;
  const canFitShortLabel = renderedWidth > shortLabel.length * CHARACTER_PIXELS_WIDTH;

  return {
    label,
    displayLabel: canFitFullLabel ? label : canFitShortLabel ? shortLabel : '',
  };
};

export const groupWindowsByDisplayIndex = (
  windows: WorkspaceLayoutPreset['windows']
): Map<number, WorkspaceLayoutPreset['windows']> => {
  return windows.reduce((acc, window) => {
    const existing = acc.get(window.displayIndex) ?? [];
    acc.set(window.displayIndex, [...existing, window]);
    return acc;
  }, new Map<number, WorkspaceLayoutPreset['windows']>());
};
